import * as React from "react";
import moment from "moment";
import {
  Badge,
  Stack,
  Button,
  Box,
  useMediaQuery,
  Typography,
} from "@mui/material";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { PurchaseSummaryModal } from "./PurchaseSummaryModal";
import { PurchaseVersionModal } from "./PurchaseVersionModal";
import { useSelector } from "react-redux";
import { Import_Purchase_Summary_GetRow } from "../../Server/ApiV2";
import Header from "./Components/Header";
import CmpModal from "../../Component/Common/ReuseableComponent/CmpModal";
import { getStatusStyleV1 } from "../../Utility/Utilitys";

const Status = ["All", "Enter", "Post"];

export function ImportPurchase() {
  const apiRef = useGridApiRef();
  const isMobile = useMediaQuery("(max-width:650px)");

  const vRdxuserLoc = useSelector((state) => state.SliceGRNLocation.value);
  const { UserID } = useSelector((state) => state.login.value);

  const [openPurchaseSummary, setOpenPurchaseSummary] = React.useState(false);
  const [openVersion, setOpenVersion] = React.useState(false);
  const [openFilterModal, setOpenFilterModal] = React.useState(false);
  const [summaryRows, setSummaryRows] = React.useState([]);
  const [selectedRow, setSelectedRow] = React.useState({});

  const [filters, setFilters] = React.useState(() => ({
    fromDate: moment().startOf("month"),
    toDate: moment(),
    location: vRdxuserLoc[0]?.UID,
    status: Status[0],
  }));

  const handleChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const onLoad = async () => {
    const Res = await Import_Purchase_Summary_GetRow({
      FromDate: moment(filters?.fromDate).format("MM/DD/YYYY"),
      ToDate: moment(filters?.toDate).format("MM/DD/YYYY"),
      TransLoc: filters?.location,
      PurStatus: filters?.status,
      UserCd: UserID,
    });
    if (Res?.status === 200) setSummaryRows(Res?.data?.ResultSet || []);
  };

  const updatedSelectedRow = React.useMemo(() => {
    if (!selectedRow?.TransId) return {};
    return (
      summaryRows.length > 0 &&
      summaryRows?.find((obj) => obj?.TransId === selectedRow?.TransId)
    );
  }, [summaryRows, selectedRow]);

  React.useEffect(() => {
    onLoad();
  }, []);

  return (
    <div className="div-container">
      <Box spacing={2}>
        {isMobile ? (
          <Stack
            direction={"row"}
            spacing={{ xs: 0.5, md: 1 }}
            flexWrap={"wrap"}
            justifyContent={"space-between"}
          >
            <Typography
              variant={"subtitle1"}
              gutterBottom
              color={"primary"}
              sx={{ fontWeight: 600, mt: 1 }}
            >
              Purchase
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                setOpenFilterModal(true);
              }}
            >
              filter
            </Button>
          </Stack>
        ) : (
          <Header
            filters={filters}
            onLoad={onLoad}
            handleChange={handleChange}
            apiRef={apiRef}
            status={Status}
            vRdxuserLoc={vRdxuserLoc}
          />
        )}
        <Box
          sx={{
            height: { xs: "70vh", sm: "66vh", md: "70vh" },
            marginTop: "20px",
          }}
        >
          <DataGridPro
            apiRef={apiRef}
            rowHeight={30}
            headerHeight={40}
            getRowId={(row) => row?.TransId}
            onRowClick={(params) => {
              setSelectedRow((prev) => ({ ...prev, ...params.row }));
              setOpenVersion(true);
            }}
            rows={summaryRows ?? []}
            columns={[
              {
                field: "action",
                headerName: "Action",
                width: 80,
                align: "center",
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                  <Button
                    size="small"
                    endIcon={<LaunchRoundedIcon />}
                    onClick={(e) => {
                      e?.stopPropagation();
                      setSelectedRow(params.row);
                      setOpenPurchaseSummary(true);
                    }}
                  >
                    View
                  </Button>
                ),
              },
              {
                field: "version",
                headerName: "Version",
                headerAlign: "center",
                align: "center",
                width: 70,
                renderCell: (params) => (
                  <Badge
                    color="primary"
                    badgeContent={Number(params.value) ?? 0}
                    showZero
                  ></Badge>
                ),
              },
              {
                field: "TransId",
                headerName: "Order ID",
                width: 70,
                align: "center",
              },
              {
                field: "Date",
                headerName: "Order Date",
                width: 95,
                align: "center",
                valueFormatter: ({ value }) =>
                  moment(value).isValid()
                    ? moment(value).format("DD/MM/YYYY")
                    : "",
              },
              {
                field: "ExpiredDate",
                headerName: "Expiry Date",
                width: 95,
                align: "center",
                valueFormatter: ({ value }) =>
                  moment(value).isValid()
                    ? moment(value).format("DD/MM/YYYY")
                    : "",
              },
              {
                field: "TransLoc_Name",
                headerName: "Order Location",
                width: 120,
              },
              {
                field: "ForecastDays",
                headerName: "Forecast Days",
                width: 105,
                align: "center",
              },
              {
                field: "GroupName",
                headerName: "Group Name",
                minWidth: 200,
                flex: 1,
              },
              {
                field: "Pur_Status",
                headerName: "Purchase Status",
                width: 120,
                align: "center",
                renderCell: (params) => {
                  const style = getStatusStyleV1(params.value);
                  return (
                    <div
                      style={{
                        ...style,
                        padding: "2px 8px",
                        borderRadius: "4px",
                        textAlign: "center",
                        width: "100%",
                        fontWeight: "bold",
                      }}
                    >
                      {params.value}
                    </div>
                  );
                },
              },
              {
                field: "Close_Status",
                headerName: "Close Status",
                width: 100,
                align: "center",
                renderCell: (params) => {
                  const style = getStatusStyleV1(params.value);
                  return (
                    <div
                      style={{
                        ...style,
                        padding: "2px 8px",
                        borderRadius: "4px",
                        textAlign: "center",
                        width: "100%",
                        fontWeight: "bold",
                      }}
                    >
                      {params.value}
                    </div>
                  );
                },
              },
              {
                field: "PurBy",
                headerName: "Purchase By",
                width: 110,
                align: "center",
              },
              {
                field: "PurDt",
                headerName: "Purchase Date",
                width: 100,
                align: "center",
                valueFormatter: ({ value }) =>
                  moment(value).isValid()
                    ? moment(value).format("DD/MM/YYYY")
                    : "",
              },
            ]}
            hideFooter
            sx={{
              border: 1,
              borderColor: "primary.light",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#0d73bb",
                color: "white",
              },
            }}
          />
        </Box>
      </Box>
      <PurchaseSummaryModal
        open={openPurchaseSummary}
        onClose={() => {
          setOpenPurchaseSummary(false);
        }}
        header={updatedSelectedRow}
        importPurchaseRows={summaryRows}
        onLoad={onLoad}
        isMobile={isMobile}
        UserCd={UserID}
      />
      <PurchaseVersionModal
        open={openVersion}
        onClose={() => setOpenVersion(false)}
        header={updatedSelectedRow}
        isMobile={isMobile}
        UserCd={UserID}
        onLoad={onLoad}
      />
      <CmpModal
        open={openFilterModal}
        onClose={() => setOpenFilterModal(false)}
        title="Filter"
        width={"95%"}
        height={"auto"}
        component={
          <Header
            filters={filters}
            onLoad={onLoad}
            handleChange={handleChange}
            apiRef={apiRef}
            status={Status}
            vRdxuserLoc={vRdxuserLoc}
          />
        }
      />
    </div>
  );
}
